/**
 * Compliance → DPIA (Art 35) - Phase 8 batch G item 1.
 *
 * Data Protection Impact Assessment register. Workflow:
 *   draft → reviewed → approved → archived
 *                   ↓
 *                rejected (terminal)
 *
 * 2-person discipline: reviewer must differ from approver (service-side
 * guard). Risk-register entries inline; the assessment narrative is the
 * regulator-facing audit trail.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw, Loader2, AlertTriangle, CheckCircle2, Clock, XCircle, Plus,
  ShieldCheck, Eye, Archive as ArchiveIcon, Send, Edit3,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listDpias, createDpia, transitionDpia,
  getDpiaScaffold, downloadDpiaDocument,
  type DpiaAssessment, type DpiaStatus, type DpiaLegalBasis, type DpiaRiskItem,
} from '@/lib/api';
import { Download, FileText, Sparkles } from 'lucide-react';

const STATUS_VARIANT: Record<DpiaStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  draft: 'glass', reviewed: 'warning', approved: 'success', rejected: 'critical', archived: 'glass',
};
const LEGAL_BASES: ReadonlyArray<{ value: DpiaLegalBasis; label: string }> = [
  { value: 'consent', label: 'Consent (Art 6(1)(a))' },
  { value: 'contract', label: 'Contractual necessity (Art 6(1)(b))' },
  { value: 'legal_obligation', label: 'Legal obligation (Art 6(1)(c))' },
  { value: 'vital_interests', label: 'Vital interests (Art 6(1)(d))' },
  { value: 'public_task', label: 'Public task (Art 6(1)(e))' },
  { value: 'legitimate_interests', label: 'Legitimate interests (Art 6(1)(f))' },
];

interface RiskRow { id: string; description: string; likelihood: 'low' | 'medium' | 'high'; impact: 'low' | 'medium' | 'high'; residualRisk: 'low' | 'medium' | 'high'; mitigation: string }
function emptyRisk(): RiskRow {
  return {
    id: `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    description: '', likelihood: 'medium', impact: 'medium', residualRisk: 'medium', mitigation: '',
  };
}

export function ComplianceDpia(): JSX.Element {
  const [items, setItems] = useState<DpiaAssessment[]>([]);
  const [counts, setCounts] = useState({ draft: 0, reviewed: 0, approved: 0, rejected: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<DpiaAssessment | null>(null);
  const [tab, setTab] = useState<'queue' | 'new'>('queue');
  // Where the keyboard goes when a press changes the tab under it. Without
  // this the pressed button is hidden or unmounted and focus falls to the body.
  const [focusNext, setFocusNext] = useState<'queue' | 'title' | 'ask' | null>(null);
  const queueTabRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const askRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusNext === 'queue') queueTabRef.current?.focus();
    if (focusNext === 'title') titleRef.current?.focus();
    if (focusNext === 'ask') askRef.current?.focus();
    if (focusNext) setFocusNext(null);
  }, [focusNext]);
  const openForm = () => {
    setTab('new');
    setFocusNext('title');
  };
  // "Start from inventory" writes over the form. With the form on its own tab
  // that can happen out of sight, so it is asked first when something is typed.
  const [askReplace, setAskReplace] = useState(false);

  // Create form
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [basis, setBasis] = useState<DpiaLegalBasis>('legitimate_interests');
  const [cats, setCats] = useState('');
  const [estimate, setEstimate] = useState('');
  const [mitigation, setMitigation] = useState('');
  const [risks, setRisks] = useState<RiskRow[]>([emptyRisk()]);
  const [consDpo, setConsDpo] = useState(false);
  const [consLegal, setConsLegal] = useState(false);
  const [consIt, setConsIt] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await listDpias({}); setItems(r.dpias); setCounts(r.byStatus); }
    catch (e) { setError(e instanceof Error ? e.message : 'failed to load DPIAs'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  // Wave 1 (2026-05-27) - pre-fill the new-DPIA form from the
  // tenant's discovered inventory. Backend route already existed at
  // /v1/compliance/dpia/scaffold but no UI surface called it.
  const [scaffolding, setScaffolding] = useState(false);
  async function handleScaffoldFromInventory(replace = false): Promise<void> {
    // Anything typed, in any field the draft would write over: the title and
    // purpose, and equally a risk somebody has described or a category list.
    const typed =
      [title, purpose, cats, estimate, mitigation].some((v) => v.trim() !== '') ||
      risks.some((r) => r.description.trim() !== '' || r.mitigation.trim() !== '');
    if (!replace && typed) {
      setAskReplace(true);
      setTab('new');
      setFocusNext('ask');
      return;
    }
    setAskReplace(false);
    setScaffolding(true);
    setError(null);
    try {
      const s = await getDpiaScaffold();
      if (s.title) setTitle(s.title);
      if (s.purpose) setPurpose(s.purpose);
      if (s.legalBasis) setBasis(s.legalBasis as DpiaLegalBasis);
      if (s.dataCategories?.length) setCats(s.dataCategories.join(', '));
      if (s.affectedSubjectsEstimate != null) setEstimate(String(s.affectedSubjectsEstimate));
      if (s.riskRegister?.length) {
        setRisks(
          s.riskRegister.map((r) => ({
            id: r.id,
            description: r.description,
            likelihood: r.likelihood,
            impact: r.impact,
            residualRisk: r.residualRisk,
            mitigation: r.mitigation ?? '',
          })),
        );
      }
      openForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'scaffold failed');
    } finally {
      setScaffolding(false);
    }
  }

  async function handleCreate() {
    if (!title.trim() || !purpose.trim()) { setError('Title and processing purpose are required'); return; }
    const cleanRisks = risks.filter((r) => r.description.trim().length > 0)
      .map<DpiaRiskItem>((r) => ({
        id: r.id, description: r.description.trim(),
        likelihood: r.likelihood, impact: r.impact, residualRisk: r.residualRisk,
        ...(r.mitigation.trim() ? { mitigation: r.mitigation.trim() } : {}),
      }));
    if (cleanRisks.length === 0) { setError('At least one risk-register entry is required'); return; }
    setSubmitting(true); setError(null);
    try {
      const dataCats = cats.split(',').map((c) => c.trim()).filter(Boolean);
      await createDpia({
        title: title.trim(), processingPurpose: purpose.trim(), legalBasis: basis,
        dataCategories: dataCats, riskRegister: cleanRisks,
        ...(estimate.trim() ? { affectedSubjectsEstimate: parseInt(estimate, 10) } : {}),
        ...(mitigation.trim() ? { mitigationPlan: mitigation.trim() } : {}),
        consultationDpo: consDpo, consultationLegal: consLegal, consultationItAdmin: consIt,
      });
      setTitle(''); setPurpose(''); setCats(''); setEstimate(''); setMitigation('');
      setRisks([emptyRisk()]); setConsDpo(false); setConsLegal(false); setConsIt(false);
      setAskReplace(false);
      setTab('queue');
      setFocusNext('queue');
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : 'create failed'); }
    finally { setSubmitting(false); }
  }

  async function handleAction(d: DpiaAssessment, action: 'review' | 'approve' | 'reject' | 'archive') {
    let reason: string | null = null;
    if (action === 'reject') {
      reason = window.prompt('Rejection reason (required):');
      if (!reason) return;
    } else if (action === 'approve') {
      reason = window.prompt('Approval note (optional - press OK to confirm):');
      if (reason === null) return;
    }
    try {
      await transitionDpia(d.id, { action, ...(reason ? { reason } : {}) });
      await refresh();
    } catch (e) { setError(e instanceof Error ? e.message : `${action} failed`); }
  }

  const sorted = useMemo(() => [...items].sort((a, b) => {
    const ord = (s: DpiaStatus) => ({ draft: 0, reviewed: 1, approved: 2, rejected: 3, archived: 4 }[s]);
    return ord(a.status) - ord(b.status);
  }), [items]);

  return (
    <PageContainer>
      <PageHeader
        title="Data Protection Impact Assessments"
        subtitle="DPIA register for high-risk processing (GDPR Art 35, DPDPA §10(2)(c)). Workflow: draft → reviewed → approved (or rejected) → archived. 2-person rule: approver must differ from reviewer."
        badges={[
          { label: 'GDPR Art 35 · DPDPA §10(2)(c)', variant: 'glass' },
          { label: 'DPO-gated', variant: 'glass' },
          { label: '2-person approval', variant: 'glass' },
          ...(loading ? [{ label: 'Loading', variant: 'glass' as const }]
            : error ? [{ label: 'Load error', variant: 'warning' as const }]
            : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="glass"
              data-action="disc.comp_dpia.a01"
              onClick={() => void handleScaffoldFromInventory()}
              disabled={scaffolding}
              title="Pre-fill the new-DPIA form from your discovered inventory"
            >
              <Sparkles className={`h-3.5 w-3.5 mr-1 ${scaffolding ? 'animate-pulse' : ''}`} />
              {scaffolding ? 'Scaffolding…' : 'Start from inventory'}
            </Button>
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        }
      />
      {error && (
        <ErrorState
          title="DPIA action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
        <KpiCard label="Draft" value={counts.draft} icon={Edit3} glow={counts.draft > 0 ? 'medium' : 'low'} hint="awaiting review" />
        <KpiCard label="Reviewed" value={counts.reviewed} icon={Clock} glow={counts.reviewed > 0 ? 'medium' : 'low'} hint="awaiting approval" />
        <KpiCard label="Approved" value={counts.approved} icon={CheckCircle2} glow={counts.approved > 0 ? 'high' : 'low'} hint="live + auditable" />
        <KpiCard label="Rejected" value={counts.rejected} icon={XCircle} glow={counts.rejected > 0 ? 'critical' : 'low'} hint="see reason" />
        <KpiCard label="Archived" value={counts.archived} icon={ArchiveIcon} glow="low" hint="closed out" />
      </div>
      {/* The queue first, the form on its own tab. Side by side, the form was
          680 px tall and pushed the page a third of a screen past the queue.
          The form stays mounted, so its action hooks are in the page from the
          start and "Start from inventory" can fill it before it is opened. */}
      <Tabs value={tab} onValueChange={(next) => setTab(next === 'new' ? 'new' : 'queue')} className="space-y-4">
        <TabsList aria-label="DPIA sections">
          <TabsTrigger value="queue" data-testid="dpia-tab-queue" ref={queueTabRef}>
            Queue{items.length > 0 ? ` (${items.length})` : ''}
          </TabsTrigger>
          <TabsTrigger value="new" data-testid="dpia-tab-new">New DPIA</TabsTrigger>
        </TabsList>
        <TabsContent value="new" forceMount hidden={tab !== 'new'} className="mt-0">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> New DPIA</CardTitle>
            <CardDescription clamp>Fill the narrative + at least one risk-register row. Status starts at draft.</CardDescription>
          </CardHeader>
          {askReplace && (
            <div
              role="alert"
              ref={askRef}
              tabIndex={-1}
              data-testid="dpia-replace-ask"
              className="mb-3 flex flex-wrap items-center gap-2 rounded border border-warning/40 bg-warning/10 p-2 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="flex-1">Starting from the inventory replaces what is typed below.</span>
              <Button size="sm" variant="glass" onClick={() => void handleScaffoldFromInventory(true)}>
                Replace it
              </Button>
              <Button
                size="sm"
                variant="glass"
                onClick={() => {
                  // The button is about to go: the keyboard returns to the form.
                  setAskReplace(false);
                  setFocusNext('title');
                }}
              >
                Keep what I typed
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-3 text-xs">
          <div className="space-y-3">
            <div><label className="text-muted-foreground block mb-0.5">Title</label>
              <input type="text" ref={titleRef} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            <div><label className="text-muted-foreground block mb-0.5">Processing purpose</label>
              <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3} maxLength={4000}
                placeholder="What you process and why. Required for the regulator audit."
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-muted-foreground block mb-0.5">Legal basis</label>
                <select value={basis} onChange={(e) => setBasis(e.target.value as DpiaLegalBasis)}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40">
                  {LEGAL_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select></div>
              <div><label className="text-muted-foreground block mb-0.5">Affected subjects (estimate)</label>
                <input type="number" min="0" value={estimate} onChange={(e) => setEstimate(e.target.value)}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            </div>
            <div><label className="text-muted-foreground block mb-0.5">Data categories (comma-separated)</label>
              <input type="text" value={cats} onChange={(e) => setCats(e.target.value)}
                placeholder="aadhaar, pan, minor_dob"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-muted-foreground">Risk register (≥1)</label>
                <Button size="sm" variant="glass" onClick={() => setRisks([...risks, emptyRisk()])}>
                  <Plus className="h-3 w-3 mr-1" /> Add risk
                </Button>
              </div>
              <div className="space-y-2">
                {risks.map((r, i) => (
                  <div key={r.id} className="glass-lite rounded p-2 space-y-1">
                    <input type="text" value={r.description}
                      onChange={(e) => setRisks(risks.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                      placeholder="Risk description"
                      className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" />
                    <div className="grid grid-cols-3 gap-1">
                      {(['likelihood', 'impact', 'residualRisk'] as const).map((field) => (
                        <select key={field} value={r[field]}
                          onChange={(e) => setRisks(risks.map((x, j) => j === i ? { ...x, [field]: e.target.value as 'low' | 'medium' | 'high' } : x))}
                          className="w-full glass-lite text-[10px] rounded px-1 py-0.5 bg-background/40 border border-border/40">
                          {['low', 'medium', 'high'].map((v) => <option key={v} value={v}>{field.slice(0, 4)}: {v}</option>)}
                        </select>
                      ))}
                    </div>
                    <input type="text" value={r.mitigation}
                      onChange={(e) => setRisks(risks.map((x, j) => j === i ? { ...x, mitigation: e.target.value } : x))}
                      placeholder="Mitigation (optional)"
                      className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" />
                    {risks.length > 1 && (
                      <Button size="sm" variant="glass" className="float-right" onClick={() => setRisks(risks.filter((_, j) => j !== i))}>
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div><label className="text-muted-foreground block mb-0.5">Mitigation plan (overall)</label>
              <textarea value={mitigation} onChange={(e) => setMitigation(e.target.value)} rows={2} maxLength={8000}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40" /></div>
            <div className="flex gap-3 text-[11px]">
              <label className="flex items-center gap-1"><input type="checkbox" checked={consDpo} onChange={(e) => setConsDpo(e.target.checked)} /> DPO consulted</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={consLegal} onChange={(e) => setConsLegal(e.target.checked)} /> Legal consulted</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={consIt} onChange={(e) => setConsIt(e.target.checked)} /> IT Admin consulted</label>
            </div>
            <div className="flex justify-end pt-1">
              <Button size="sm" data-action="disc.comp_dpia.a02" onClick={() => void handleCreate()} disabled={submitting}>
                {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                Create DPIA
              </Button>
            </div>
          </div>
          </div>
        </GlassCard>
        </TabsContent>
        <TabsContent value="queue" className="mt-0">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle>DPIA queue</CardTitle>
            <CardDescription clamp>Sorted by status. Click to view full assessment.</CardDescription>
          </CardHeader>
          {loading && <LoadingState caption="Loading…" />}
          {!loading && sorted.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <Edit3 className="h-6 w-6 mx-auto mb-2 opacity-40" /><div>No DPIAs on file.</div>
              <Button size="sm" variant="glass" className="mt-3" onClick={openForm}>
                <Plus className="h-3.5 w-3.5 mr-1" /> New DPIA
              </Button>
            </div>
          )}
          {!loading && sorted.length > 0 && (
            <div className="space-y-2">
              {sorted.map((d, i) => (
                <motion.div key={d.id} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }} className="glass-lite rounded p-3 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant={STATUS_VARIANT[d.status]} className="text-[10px]">{d.status}</Badge>
                        <span className="font-medium truncate">{d.title}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">{d.processingPurpose.slice(0, 120)}{d.processingPurpose.length > 120 ? '…' : ''}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        basis: {d.legalBasis.replace(/_/g, ' ')} · risks: {d.riskRegister.length} · categories: {d.dataCategories.length}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="glass" onClick={() => setDetail(d)} title="Details"><Eye className="h-3 w-3" /></Button>
                      <Button
                        size="sm"
                        variant="glass"
                        data-action="disc.comp_dpia.a04"
                        onClick={() => downloadDpiaDocument(d.id, 'pdf')}
                        title="Download regulator-grade PDF"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="glass"
                        data-action="disc.comp_dpia.a05"
                        onClick={() => downloadDpiaDocument(d.id, 'html')}
                        title="Open as HTML"
                      >
                        <FileText className="h-3 w-3" />
                      </Button>
                      {d.status === 'draft' && <Button size="sm" variant="glass" data-action="disc.comp_dpia.a03" onClick={() => void handleAction(d, 'review')} title="Mark reviewed"><Clock className="h-3 w-3" /></Button>}
                      {d.status === 'reviewed' && <Button size="sm" variant="glass" data-action="disc.comp_dpia.a03" onClick={() => void handleAction(d, 'approve')} title="Approve"><ShieldCheck className="h-3 w-3" /></Button>}
                      {(d.status === 'draft' || d.status === 'reviewed') && <Button size="sm" variant="glass" data-action="disc.comp_dpia.a03" onClick={() => void handleAction(d, 'reject')} title="Reject"><XCircle className="h-3 w-3" /></Button>}
                      {(d.status === 'approved' || d.status === 'rejected') && <Button size="sm" variant="glass" data-action="disc.comp_dpia.a03" onClick={() => void handleAction(d, 'archive')} title="Archive"><ArchiveIcon className="h-3 w-3" /></Button>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </GlassCard>
        </TabsContent>
      </Tabs>
      <Dialog open={detail !== null} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{detail?.title ?? ''}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Status</div><Badge variant={STATUS_VARIANT[detail.status]} className="text-[10px]">{detail.status}</Badge></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Legal basis</div><code className="font-mono">{detail.legalBasis}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Subjects affected (est.)</div><code className="font-mono">{detail.affectedSubjectsEstimate ?? '-'}</code></div>
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Consultations</div><div>{[detail.consultationDpo ? 'DPO' : null, detail.consultationLegal ? 'Legal' : null, detail.consultationItAdmin ? 'IT Admin' : null].filter(Boolean).join(' · ') || 'none'}</div></div>
              </div>
              <div className="glass-lite rounded p-2">
                <div className="text-muted-foreground mb-0.5">Processing purpose</div>
                <div className="whitespace-pre-wrap">{detail.processingPurpose}</div>
              </div>
              <div>
                <div className="font-medium mb-1">Risk register ({detail.riskRegister.length})</div>
                <div className="space-y-1">
                  {detail.riskRegister.map((r) => (
                    <div key={r.id} className="glass-lite rounded p-2">
                      <div>{r.description}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        likelihood: {r.likelihood} · impact: {r.impact} · residual: {r.residualRisk}
                        {r.mitigation && <> · mitigation: <em>{r.mitigation}</em></>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {detail.mitigationPlan && (
                <div className="glass-lite rounded p-2"><div className="text-muted-foreground mb-0.5">Mitigation plan</div><div className="whitespace-pre-wrap">{detail.mitigationPlan}</div></div>
              )}
              {detail.reviewedByActorId && (
                <div className="glass-lite rounded p-2 text-muted-foreground">Reviewed by <code className="font-mono">{detail.reviewedByActorId.slice(0, 8)}…</code> at {detail.reviewedAt ? new Date(detail.reviewedAt).toLocaleString() : '?'}</div>
              )}
              {detail.approverActorId && (
                <div className="glass-lite rounded p-2 text-muted-foreground">Approved by <code className="font-mono">{detail.approverActorId.slice(0, 8)}…</code> at {detail.approvedAt ? new Date(detail.approvedAt).toLocaleString() : '?'}</div>
              )}
              {detail.rejectionReason && (
                <div className="glass-lite border border-sev-critical/40 rounded p-2 text-sev-critical"><div className="font-medium">Rejected</div><div>{detail.rejectionReason}</div></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
